import React, { useEffect, useRef, useState } from "react";
import "../assets/templates/Mosaic.css";
import { getLanguagePack } from "../service/service.js";
import LessonInstruction from "../containers/instruction/LessonInstruction.js";
import {getBrowser, getOS} from "../lib/utils";
import useAudioPlayback from "../hooks/useAudioPlayback";
import { useLessonNavigation } from "../hooks/useLessonNavigation";

const Mosaic = ({
  blockData,
  contentIndex,
  second,
  en,
  audioUrl
}) => {
  const [cardData, setCardData] = useState([]);
  const languagePackage = getLanguagePack();
  const pendingAdvanceRef = useRef(false);

  const { audioRef, setAudioSrc } = useAudioPlayback(audioUrl, 'mosaic');
  const { next, clearNextTimeout } = useLessonNavigation();

  useEffect(() => {
    setCardData(
      getData(getCurrentContent(blockData.content)).map((item) => ({
        ...item,
        flipped: false,
        visited: false,
      }))
    );
    pendingAdvanceRef.current = false;
    clearNextTimeout();
  }, [contentIndex, blockData, clearNextTimeout]);

  useEffect(() => {
    const currentAudio = audioRef.current;

    const handleAudioEnded = () => {
      if (!pendingAdvanceRef.current) {
        return;
      }

      pendingAdvanceRef.current = false;
      next();
    };

    currentAudio.addEventListener("ended", handleAudioEnded);

    return () => {
      currentAudio.removeEventListener("ended", handleAudioEnded);
    };
  }, [audioRef, next]);

  const splitContent = (data) => {
    let lastType = "";
    return data.reduce((accumulator, currentValue, currentIndex) => {
      const content = { ...currentValue, contentIndex: currentIndex };
      if (currentValue.type === "instruction") {
        lastType = "instruction";
        return [...accumulator, [content]];
      }
      if (accumulator.length > 0 && lastType === "content") {
        accumulator[accumulator.length - 1].push(content);
      } else {
        accumulator.push([content]);
      }
      lastType = "content";
      return accumulator;
    }, []);
  };

  const getCurrentContent = (data) => {
    const splittedData = splitContent(data);
    return (
      splittedData.find((value) =>
        value.some((item) => item.contentIndex === contentIndex)
      ) || []
    );
  };

  const getData = (data) => data.filter((e) => e.type !== "instruction");

  const flipped = (index) => {
    setCardData((prevCardData) => {
      const newCardData = [...prevCardData];
      const selectedCard = {
        ...newCardData[index],
        flipped: !newCardData[index].flipped,
        visited: true,
      };
      newCardData[index] = selectedCard;

      if (selectedCard.flipped) {
        const soundUrl =
          getBrowser() === "Safari" || getOS() === "iOS"
            ? `${process.env.REACT_APP_CLOUDFRONT_DNS}/media/audios/${selectedCard.sound}`
            : `${process.env.REACT_APP_CLOUDFRONT_DNS}/media/audios/${selectedCard.sound_mp3}`;

        setAudioSrc(soundUrl);

        if (newCardData.every((item) => item.visited)) {
          pendingAdvanceRef.current = true;
        }
      }

      return newCardData;
    });
  };

  const _getMessage = (message) => languagePackage[message] || message;

  return (
    <div className="new-bg-color mosaic-container">
      {blockData.content[contentIndex] &&
        blockData.content[contentIndex].type === "instruction" ? (
        <LessonInstruction
          instructionText={blockData.content[contentIndex].instruction}
          audioUrl={audioUrl}
        />
      ) : (
        <section className="mosaic-section">
          <h4 className="mosaic-title">
            {second
              ? en
                ? _getMessage("Tap on the card, listen, and speak.")
                : _getMessage("Tap on the card, listen, and speak in English.")
              : _getMessage("Tap the image to see the word")}
          </h4>
          <div className="mosaic-grid">
            {cardData.map((item, index) => (
              <div key={index} className="mosaic-card">
                <div
                  style={{ display: !item.flipped ? "block" : "none" }}
                  onClick={() => flipped(index)}
                >
                  <img
                    className="mosaic-image"
                    src={`${process.env.REACT_APP_CLOUDFRONT_DNS}/media/images/${item.picture}`}
                    alt="Mosaic"
                  />
                </div>
                <div
                  style={{
                    display: item.flipped ? "block" : "none",
                    backgroundImage: `url(${process.env.REACT_APP_CLOUDFRONT_DNS}/media/images/${item.picture})`,
                    backgroundSize: "cover",
                  }}
                  className={
                    item.flipped
                      ? "mosaic-card-back mosaic-card-back-flip"
                      : "mosaic-card-front-flip"
                  }
                  onClick={() => flipped(index)}
                >
                  <div className="mosaic-card-itens-back">
                    <h4 style={{ whiteSpace: "pre-line" }}>
                      {en
                        ? item.en.replace(/<br>/g, "\n")
                        : item.pt.replace(/<br>/g, "\n")}
                    </h4>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Mosaic;
